/**
 * BifrostBridge - Singleton that manages native bridge communication
 *
 * This class handles the actual WebView bridge connection and coordinates
 * message passing between multiple BifrostWeb client instances.
 */

import {
  RpcEnvelope,
  RpcResponse,
  CallOptions,
  DEFAULT_READY_TIMEOUT_MS,
} from "../types/protocol";
import { RpcCallError } from "../types/errors";
import { NativeHandshake } from "./NativeHandshake";

// Forward declaration for BifrostWeb
export interface IBifrostClient {
  readonly clientId: string;
  readonly isDestroyed: boolean;
  hasService(serviceName: string): boolean;
  handleIncomingCall(envelope: RpcEnvelope): Promise<void>;
  handleIncomingNotification(envelope: RpcEnvelope): void;
  /**
   * The shared handshake completed. Called on every registered client, including
   * ones whose own `initialize()` already rejected: the watch they started can be
   * extended by a later caller and complete after they gave up, leaving them
   * unusable against a working bridge.
   *
   * This does not replay a call that already failed — only makes subsequent ones
   * possible without a second `initialize()`.
   */
  markBridgeReady?(): void;
}

interface PendingCall {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
  clientId: string; // Track which client initiated this call
}

/**
 * Singleton bridge that manages communication with the native side
 */
export class BifrostBridge {
  private static instance: BifrostBridge;

  private clients = new Map<string, IBifrostClient>();
  private pendingCalls = new Map<string, PendingCall>();
  private handlerInitialized = false;
  private isDestroyed = false;

  /**
   * Owns the handshake outright: which shim a send and an ack belong to, host
   * re-entrancy, the deadline, the shared attempt, and when evaluation is scheduled.
   * This class only awaits an outcome, forwards `native_ready`, and stops it.
   */
  private handshake = new NativeHandshake({
    isDestroyed: () => this.isDestroyed,
    onCompleted: () => this.markClientsReady(),
  });

  private constructor() {
    this.setupGlobalHandler();
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): BifrostBridge {
    if (!BifrostBridge.instance || BifrostBridge.instance.isDestroyed) {
      BifrostBridge.instance = new BifrostBridge();
    }
    return BifrostBridge.instance;
  }

  /**
   * Initialize the bridge connection
   */
  async initialize(
    readyTimeout: number = DEFAULT_READY_TIMEOUT_MS,
  ): Promise<void> {
    if (this.isDestroyed) {
      throw new Error("BifrostBridge has been destroyed");
    }
    // Wait for native bridge to be ready
    await this.handshake.await(readyTimeout);
  }

  /**
   * Register a client with the bridge
   */
  registerClient(client: IBifrostClient): void {
    if (this.isDestroyed) {
      throw new Error("BifrostBridge has been destroyed");
    }

    this.clients.set(client.clientId, client);
  }

  /**
   * Unregister a client from the bridge
   */
  unregisterClient(clientId: string): void {
    // Remove client
    this.clients.delete(clientId);

    // Cancel any pending calls for this client
    for (const [callId, pendingCall] of this.pendingCalls) {
      if (pendingCall.clientId === clientId) {
        clearTimeout(pendingCall.timeout);
        pendingCall.reject(new Error("Client instance destroyed"));
        this.pendingCalls.delete(callId);
      }
    }

    // If no more clients, cleanup global handler
    if (this.clients.size === 0) {
      this.cleanup();
    }
  }

  /**
   * Send an RPC call through the bridge
   */
  async sendCall(
    clientId: string,
    callId: string,
    service: string,
    method: string,
    payload: any,
    options?: CallOptions,
    envelope?: RpcEnvelope,
  ): Promise<any> {
    if (this.isDestroyed) {
      throw new Error("BifrostBridge has been destroyed");
    }

    // Use provided envelope or create a new one
    const finalEnvelope: RpcEnvelope = envelope || {
      id: callId,
      kind: "call",
      service,
      method,
      payload,
      ts: Date.now(),
    };

    return new Promise<any>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingCalls.delete(callId);
        reject(new Error("RPC call timeout"));
      }, options?.timeout ?? 5000);

      this.pendingCalls.set(callId, {
        resolve,
        reject,
        timeout,
        clientId,
      });

      this.sendToNative(finalEnvelope);
    });
  }

  /**
   * Send a notification through the bridge
   */
  sendNotification(
    service: string,
    method: string,
    payload: any,
    callId: string,
  ): void {
    if (this.isDestroyed) {
      return;
    }

    const envelope: RpcEnvelope = {
      id: callId,
      kind: "notify",
      service,
      method,
      payload,
      ts: Date.now(),
    };

    this.sendToNative(envelope);
  }

  /**
   * Send a response back to native
   */
  sendResponse(response: RpcResponse): void {
    if (this.isDestroyed) {
      return;
    }

    this.sendToNative(response);
  }

  /**
   * Setup global message handler
   */
  private setupGlobalHandler(): void {
    if (this.handlerInitialized || typeof window === "undefined") {
      return;
    }

    window.bifrostHandleN2W = (message: any) => {
      this.handleGlobalMessage(message);
    };

    this.handlerInitialized = true;
  }

  /**
   * Handle incoming messages from native
   */
  private async handleGlobalMessage(message: any): Promise<void> {
    if (this.isDestroyed) {
      return;
    }

    try {
      if (this.isResponse(message)) {
        this.handleResponse(message);
      } else if (this.isCall(message)) {
        await this.handleIncomingCall(message);
      } else if (this.isNotification(message)) {
        this.handleIncomingNotification(message);
      } else if (this.handshake.isNativeReady(message)) {
        this.handshake.handleNativeReady(message);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[BifrostBridge] Error handling global message:", error);
    }
  }

  /**
   * Handle response messages
   */
  private handleResponse(response: RpcResponse): void {
    const pendingCall = this.pendingCalls.get(response.id);
    if (!pendingCall) {
      return; // Response for unknown call, might be timeout
    }

    this.pendingCalls.delete(response.id);
    clearTimeout(pendingCall.timeout);

    if (response.error) {
      const error = new RpcCallError(response.error);
      pendingCall.reject(error);
    } else {
      pendingCall.resolve(response.result);
    }
  }

  /**
   * Handle incoming calls from native
   */
  private async handleIncomingCall(envelope: RpcEnvelope): Promise<void> {
    // Broadcast to all active clients (original behavior)
    for (const client of this.clients.values()) {
      if (!client.isDestroyed) {
        try {
          await client.handleIncomingCall(envelope);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("[BifrostBridge] Error routing call to client:", error);
        }
      }
    }
  }

  /**
   * Handle incoming notifications from native
   */
  private handleIncomingNotification(envelope: RpcEnvelope): void {
    // Broadcast notifications to all active clients
    for (const client of this.clients.values()) {
      if (!client.isDestroyed) {
        try {
          client.handleIncomingNotification(envelope);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            "[BifrostBridge] Error routing notification to client:",
            error,
          );
        }
      }
    }
  }

  /**
   * Send message to native bridge
   */
  private sendToNative(message: RpcEnvelope | RpcResponse): void {
    if (!window.bifrostNativeBridge) {
      throw new Error("Native bridge not available");
    }

    window.bifrostNativeBridge.sendToNative(message as RpcEnvelope);
  }

  /**
   * Tell every registered client the handshake landed. `initialize()` resolving is
   * not a reliable trigger for this: the caller that started the watch may have
   * rejected on its own timeout long before a later caller's extension carried it
   * to completion.
   */
  private markClientsReady(): void {
    for (const client of this.clients.values()) {
      try {
        client.markBridgeReady?.();
      } catch (error) {
        // Client code. One faulting client must not stop the rest from learning
        // the bridge is usable.
        // eslint-disable-next-line no-console
        console.error("[BifrostBridge] markBridgeReady threw:", error);
      }
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    // Cancel all pending calls
    for (const [, pendingCall] of this.pendingCalls) {
      clearTimeout(pendingCall.timeout);
      pendingCall.reject(new Error("BifrostBridge destroyed"));
    }
    this.pendingCalls.clear();

    // Clear clients
    this.clients.clear();

    // Remove global handler
    if (typeof window !== "undefined") {
      (window as any).bifrostHandleN2W = undefined;
      this.handlerInitialized = false;
    }

    this.handshake.stop();
    this.isDestroyed = true;
  }

  /**
   * Reset bridge for testing (package private)
   */
  static resetForTesting(): void {
    // @ts-ignore - Reset singleton for testing
    BifrostBridge.instance = undefined;
  }

  /**
   * Destroy the bridge (for testing purposes)
   */
  static destroy(): void {
    if (BifrostBridge.instance) {
      BifrostBridge.instance.cleanup();
      // @ts-ignore - Reset singleton for testing
      BifrostBridge.instance = undefined as any;
    }
  }

  /**
   * Check if message is a response
   */
  private isResponse(message: any): message is RpcResponse {
    return (
      message &&
      typeof message.id === "string" &&
      (message.result !== undefined || message.error !== undefined)
    );
  }

  /**
   * Check if message is a call
   */
  private isCall(message: any): message is RpcEnvelope {
    return message && message.kind === "call" && message.service;
  }

  /**
   * Check if message is a notification
   */
  private isNotification(message: any): message is RpcEnvelope {
    return message && message.kind === "notify";
  }

  /**
   * Check if message is a native ready
   */
  /**
   * Get bridge status (for debugging/testing)
   */
  getStatus() {
    return {
      clientCount: this.clients.size,
      pendingCallCount: this.pendingCalls.size,
      isDestroyed: this.isDestroyed,
      handlerInitialized: this.handlerInitialized,
    };
  }
}
